<template lang="pug">
    .stock(:class="{ block }")
        el-row.flex-row.deletable-row(:gutter="20")
            el-col
                el-form-item(:label="$t('Stock')" :prop="`stocks.${i}.id`")
                    el-select.is-full-width(
                        placeholder="" v-model="id"
                        :class="{ 'loading': options.stock.loading }"
                        :disabled="disabledStockOptions"
                    )
                        el-option(
                            v-for="(item, key) in stockOptions"
                            :disabled="item.disabled"
                            :key="key"
                            :label="$t(item.name)"
                            :value="item.id"
                        )
            el-col.icon.disabled(v-if="stock && stock.status !== 'enabled'")
                el-tooltip(effect="dark" :content="$t('Disabled in the stock settings')" placement="top")
                    i.el-icon-warning
            el-col.icon.sold-out(v-else-if="qty <= 0")
                el-tooltip(effect="dark" :content="$t('Sold out')" placement="top")
                    i.el-icon-warning-outline
            el-col.icon.enabled(v-else)
                i.el-icon-success
            el-col
                el-form-item(:label="$t('Quantity')" :prop="`stocks.${i}.pivot.qty`")
                    el-input-number(v-model="qty" :step="1" :min="0" step-strictly :precision="0")
                    el-button.delete-row(
                        circle type="info" size="mini"
                        icon="el-icon-fa-trash-alt far"
                        @click="remove"
                    )

        .settings
            .restriction.flex-row
                el-form-item.min(
                    :label="$t('restricted access')"
                    size="mini"
                )
                    el-switch(
                        :active-text="$t('use global setting')"
                        inactive-color="#ff4949"
                        v-model="globalRestriction"
                    )
                el-form-item.min(
                    :label="$t('Available for')"
                    :prop="`stocks.${i}.pivot.restriction`"
                    size="mini"
                    style="min-width: 160px"
                )
                    el-select(
                        v-model="restriction"
                        placeholder=""
                        autocomplete="no-fill"
                        :disabled="globalRestriction"
                    )
                        el-option(
                            v-for="({ label, value }) in (globalRestriction ? stockModuleOptions.restriction : options.stockPivotRestriction).items"
                            :key="value"
                            :label="$t(label)"
                            :value="value"
                        )
                transition(name="slide-decent-left")
                    el-form-item.custom-label(
                        v-if="(globalRestriction && stock.restriction === 'customer-groups') || stock.pivot.restriction === 'customer-groups'"
                        :label="$t('Customer groups')"
                        :prop="`stocks.${i}.pivot.customerGroups`"
                        size="mini"
                    )
                        //- @change="value => set({ path: 'form.customerGroups', value })"
                        el-select.is-full-width(
                            placeholder=""
                            v-model="customerGroups"
                            multiple
                            clearable
                            filterable
                            value-key="id"
                            autocomplete="no-fill"
                            :disabled="globalRestriction"
                        )
                            el-option(
                                v-for="group in options.customerGroup.items"
                                :key="group.id"
                                :label="group.name"
                                :value="group"
                            )

            .content-status-flex-row
                .content-status-flex-col.datepickers
                    el-row(:gutter="20")
                        el-col(:span="12")
                            el-form-item.is-full-width.no-margin(
                                size="mini"
                                :label="$t('sellable from')"
                                :prop="`stocks.${i}.pivot.sellable_from`"
                            )
                                el-date-picker.is-full-width(
                                    v-model="sellableFrom"
                                    type="datetime"
                                    :format="$t('yyyy-MM-dd') + ' HH:mm'"
                                    prefix-icon="far el-icon-fa-calendar-alt"
                                    :picker-options="{firstDayOfWeek:1, disabledDate: disabledSellableFromDate}"
                                )
                        el-col(:span="12")
                            el-form-item.no-margin.is-full-width(
                                size="mini"
                                :label="$t('sellable to')"
                                :prop="`stocks.${i}.pivot.sellable_to`"
                            )
                                el-date-picker.is-full-width(
                                    v-model="sellableTo"
                                    type="datetime"
                                    :format="$t('yyyy-MM-dd') + ' HH:mm'"
                                    prefix-icon="far el-icon-fa-calendar-alt"
                                    :picker-options="{firstDayOfWeek:1, disabledDate: disabledSellableToDate}"
                                )
                .content-status-flex-col.status-switch
                    el-form-item.no-margin(size="mini")
                        el-switch(
                            :active-text="$t('Active')"
                            inactive-color="#ff4949"
                            v-model="sellable"
                        )

        el-drawer(
            :title="$t('stock history')"
            :visible.sync="modal.visibility"
            :close-on-press-escape="true"
            :wrapper-closable="true"
            :modal="false"
            size="760px"
            @closed="closeModal"
        )
            .drawer-body(@scroll="handleDrawerScroll" v-loading="loading")
                table.stock-table(v-if="stockHistory && stockHistory.items.length>0")
                    thead
                        th {{ $t('time') }}
                        th {{ $t('new quantity') }}
                        th {{ $t('change') }}
                        th {{ $t('context') }}
                        th {{ $t('user') }}
                    tr(v-for="(item, index) in stockHistory.items")
                        td {{ createdAt(item.created_at) }}
                        td.center {{ item.qty }}
                        td.center
                            span(v-if="index < stockHistory.items.length-1") {{ (item.qty - stockHistory.items[index+1].qty > 0) ? '+' : '' }}
                            span(v-if="index < stockHistory.items.length-1") {{ item.qty - stockHistory.items[index+1].qty }}
                            span(v-if="index === stockHistory.items.length-1 && stockHistory.lastPage === stockHistory.currentPage") {{ "+"+item.qty }}
                            span(v-else-if="index === stockHistory.items.length-1 && !stockHistory.lastPage === stockHistory.currentPage") {{ item.qty-stockHistory.items[index+1].qty }}
                        td
                            span(v-if="item.context") {{ $t(item.context) }}
                            .source(v-if="item.source") {{ item.source }}
                        td
                            span(v-if="item.user") {{ item.user.name }}
                            span(v-else) &nbsp;
                p(v-else) {{ $t('no stock history present') }}

        el-button.mt(size="mini" @click="handleHistoryClick") {{ $t('show stock history') }}
        el-divider
</template>

<i18n>
{
    "de": {
        "Stock": "Lagerort",
        "Quantity": "Menge",
        "Disabled in the stock settings": "In den Lagereinstellungen deaktiviert",
        "Sold out": "Ausverkauft",
        "sellable from": "verkaufbar ab",
        "sellable to": "verkaufbar bis",
        "to": "bis",
        "stock history": "Bestands-Historie",
        "show stock history": "Bestands-Historie anzeigen",
        "time": "Uhrzeit",
        "new quantity": "Neuer Bestand",
        "change": "Veränderung",
        "context": "Kontext",
        "initial quantity": "Anfangsbestand",
        "user": "Benutzer",
        "no stock history present" : "Keine Bestands-Historie vorhanden",
        "save article" : "Artikel speichern",
        "partially cancel purchase": "Teilstorno einer Bestellung",
        "mark cancellation received": "Rücksendung als erhalten markiert",
        "import": "Import",
        "save product" : "Produkt speichern",
        "product import" : "Produkt-Import",
        "save stock" : "Lagerbestand speichern",
        "update article stocks" : "Update Artikel-Bestand",
        "update purchased item stocks" : "Update Bestand gekaufter Artikel",
        "sell" : "Verkauf",
        "online purchase" : "Online-Verkauf",
        "update stock" : "Update Lagerbestand",
        "purchased item undo stock changes" : "Verkaufter Artikel - Lagerbestand rückgängig",
        "Close": "Schließen",

        "restricted access": "eingeschränkter Zugriff",
        "use global setting": "Globale Einstellung verwenden",
        "Available for": "verfügbar für",
        "Customer groups": "Kundengruppen",
        "Anyone": "Jeden",
        "Guests": "Gäste",
        "Customer Groups": "Kundengruppen"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { compareAsc, compareDesc, addDays, startOfDay, fromUnixTime, getUnixTime } from 'date-fns';
import { appFromUnixTime, appGetUnixTime } from '@/utils/timezone';
import { timestamp2date } from '@/filters'

import pinia from '@/pinia';
import { useShopArticleStore, useShopStockStore } from '@/stores';

export default {
    name: 'Stock',
    data() {
        return {
            loading: false,
            stockHistory: {
                items: [],
                currentPage: 1,
                lastPage: 0
            },
            modal: {
                visibility: false
            }
        }
    },
    props: {
        form: {
            type: Object,
            required: true
        },
        rules: {
            type: Object,
            required: false
        },
        stock: {
            type: Object,
            required: true
        },
        block: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        i() {
            return _.findIndex(this.form.stocks, stock => stock.id === this.stock.id);
        },
        id: {
            get() {
                return this.stock.id;
            },
            set(id) {
                const el = this.options.stock.items.find(el => el.id === id);
                if (el) {
                    const stock = { id, name: el.name, status: el.status }
                    this.updateStock({ index: this.i, stock });
                }
            }
        },
        qty: {
            get() {
                return this.stock.pivot.qty;
            },
            set(value) {
                this.setStockQty({ stock: this.stock, value });
            }
        },
        globalRestriction: {
            get() {
                return this.stock.pivot.restriction == null
            },
            set(value) {
                let restriction = null;
                let customerGroups = []

                if (!value) {
                    restriction = this.stock.restriction || 'anyone';
                    customerGroups = this.stock.customerGroups || []
                }

                this.updateStockPivot({ index: this.i, pivot: { restriction, customerGroups }});
            }
        },
        restriction: {
            get() {
                if (this.globalRestriction) return this.stock.restriction;
                return this.stock.pivot.restriction;
            },
            set(restriction) {
                if (this.globalRestriction) return
                this.updateStockPivot({ index: this.i, pivot: { restriction }});
            }
        },
        customerGroups: {
            get() {
                if (this.globalRestriction) return this.stock.customerGroups;
                return this.stock.pivot.customerGroups;
            },
            set(customerGroups) {
                if (this.globalRestriction) return
                this.updateStockPivot({ index: this.i, pivot: { customerGroups }});
            }
        },
        sellable: {
            get() {
                let enabled = true;

                const start = this.sellableFrom;
                if (start && enabled) enabled = compareAsc(new Date(), start) > 0;

                const end = this.sellableTo;
                // if (end && enabled) enabled = compareDesc(new Date(), addDays(end, 1)) > 0
                if (end && enabled) enabled = compareDesc(new Date(), end) > 0;

                return enabled;
            },
            set(active) {
                const start = this.sellableFrom;
                const end = this.sellableTo;

                if (active) {
                    if (start && compareAsc(new Date(), start) < 0) this.sellableFrom = startOfDay(new Date());
                    else if (end && compareDesc(new Date(), end) < 0) this.sellableTo = null;
                } else {
                    this.sellableTo = startOfDay(new Date());
                }

                // if (this.currentMenuItemIndex !== null) {
                //     const i = this.currentMenuItemIndex;
                //     store.commit('menu/form/disabled', { i, value: !active });
                // }
            }
        },
        sellableFrom: {
            get() {
                const timestamp = this.stock.pivot.sellable_from;
                // return timestamp ? startOfDay(fromUnixTime(timestamp)) : '';
                return timestamp ? appFromUnixTime(timestamp) : '';
            },
            set(value) {
                value = value !== null ? appGetUnixTime(value) : null;
                this.setStockSellableFrom({ stock: this.stock, value });
            }
        },
        sellableTo: {
            get() {
                const timestamp = this.stock.pivot.sellable_to;
                // return timestamp ? startOfDay(fromUnixTime(timestamp)) : '';
                return timestamp ? appFromUnixTime(timestamp) : '';
            },
            set(value) {
                value = value !== null ? appGetUnixTime(value) : null;
                this.setStockSellableTo({ stock: this.stock, value });
            }
        },
        usedStockIDs() {
            return this.form.stocks.map(el => el.id);
        },
        stockOptions() {
            let stocks = _.cloneDeep(this.options.stock.items);
            _.map(stocks, stock => {
                stock.disabled = this.usedStockIDs.includes(stock.id) && stock.id !== this.stock.id;
            });
            return stocks;
        },
        disabledStockOptions() {
            return _.filter(this.stockOptions, 'disabled').length >= this.stockOptions.length - 1;
        },
        shopArticleStore() {
            return useShopArticleStore(pinia);
        },
        shopStockStore() {
            return useShopStockStore(pinia);
        },
        options() {
            return this.shopArticleStore.options;
        },
        stockModuleOptions() {
            return this.shopStockStore.options;
        }
    },
    methods: {
        closeModal() {
            this.modal.visibility = false;
        },
        handleHistoryClick() {
            this.modal.visibility = true
            this.stockHistory.items = []
            this.loadNew(1)
        },
        handleDrawerScroll(event) {
            const { target } = event;
            this.loadMoreIfNeeded(target)
        },
        loadMoreIfNeeded(target) {
            const currentPage = this.stockHistory.currentPage;
            if (this.loading === false && this.stockHistory.lastPage > currentPage) {

                let loadMore = false;
                const offset = 40;

                if (Math.ceil(target.scrollTop) >= target.scrollHeight - target.offsetHeight - offset) {
                    loadMore = true;
                }

                if (loadMore) {
                    this.loadNew(currentPage + 1);
                }
            }
        },
        loadNew(page) {
            this.stockHistory.currentPage = page;
            this.loading = true

            const variables = {
                stock: this.stock,
                articleId: this.form.id,
                page: page,
            }

            this.fetchArticleHistoryPaginated(variables).then(data => {
                this.stockHistory.lastPage = data.pageInfo.lastPage;
                this.stockHistory.items.push(...data.items);
            }).catch((err) => reject(err)).finally(() => {
                this.loading = false
            })
        },
        createdAt(date) {
            return timestamp2date(date, this.$t('yyyy-MM-dd') + ' HH:mm')
        },
        remove() {
            this.deleteStock(this.stock);
        },
        disabledSellableFromDate(start) {
            const end = this.sellableTo;
            if (end) return compareAsc(addDays(start, 1), end) > 0;
            else return false;
        },
        disabledSellableToDate(end) {
            const start = this.sellableFrom;
            if (start) return compareDesc(end, addDays(start, 1)) > 0;
            else return false;
        },
        fetchArticleHistoryPaginated(variables) {
            return this.shopArticleStore.fetchArticleHistoryPaginated(variables);
        },
        updateStock(payload) {
            this.shopArticleStore.updateFormStock(payload);
        },
        updateStockPivot(payload) {
            this.shopArticleStore.updateFormStockPivot(payload);
        },
        deleteStock(stock) {
            this.shopArticleStore.deleteFormStock(stock);
        },
        setStockQty(payload) {
            this.shopArticleStore.setFormStockQty(payload);
        },
        setStockSellableFrom(payload) {
            this.shopArticleStore.setFormStockSellableFrom(payload);
        },
        setStockSellableTo(payload) {
            this.shopArticleStore.setFormStockSellableTo(payload);
        }
    }
};
</script>

<style lang="sass" scoped>

.stock-table
    width: 100%
    border-collapse: collapse
    th
        background-color: $color-selection
        text-align: left
        padding: 8px 12px
        font-weight: 500
        font-size: 13px !important
        color: $--color-text-regular
        white-space: nowrap
    tr:nth-child(even)
        background: darken($--background-color-base, 1%)
    tr:nth-child(odd)
        background: lighten($--background-color-base, 1%)
    td
        padding: 6px 12px
        font-size: 13px
        &.center
            text-align: center
    .source
        font-size: 11px
        color: $--color-text-secondary
        margin-top: 2px

.stock
    margin-bottom: 18px
    &.block
        padding-left: 0
        &:before
            left: -12px
    .el-col.icon
        // max-width: 30px
        flex-grow: 0
        flex-basis: 0
        margin-top: 25px
        height: 36px
        width: 43px
        i
            height: 35px
            font-size: 23px
            line-height: 35px
        &.sold-out
            color: $--color-warning
        &.enabled
            color: $--color-success
        &.disabled
            color: $--color-danger

.flex-row
    display: flex
    gap: 0px
    & > div:not(.min)
        flex-basis: 100%
        & + .el-input
            flex-basis: 50%
            margin-left: 10px
    .el-col.min
        width: 30px
        flex-basis: 30px
        flex-grow: 0
.deletable-row button.delete-row
    position: initial
    margin-left: 10px

#app .settings
    .datepickers
        max-width: 400px
    .status-switch::v-deep
        .el-form-item__content
            line-height: 26px
    &::v-deep
        .el-form-item__label
            font-size: 0.85rem
            line-height: 1.2
            padding-bottom: 6px
            height: 20px

.accordion
    margin-top: 24px
.mt
    margin-top: 20px

.collapseContainer
    height: 120px
    overflow: scroll
    margin-right: 10px

.pl
    padding-left: 10px
    padding-right: 10px

.drawer-body
    flex-grow: 1
    padding: 20px
    padding-top: 0
    max-height: calc(100vh - 66px)
    overflow: auto

.restriction
    &.flex-row
        gap: 1rem
    &::v-deep .el-switch__label
        white-space: nowrap
        span
            font-size: 0.85rem
</style>
